import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { LoanStep, TermUnit } from 'even-ts-static'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { AprType, coAppBadgeBreakpoint, PublicClassNames, TRANSUNION_COMPANY_UUID } from 'embed/constants'
import { companyUuid as configCompanyUuid, isDev } from 'embed/constants/config'

import {
  APR_FIXED,
  APR_LABEL,
  APR_POSTFIX,
  APR_VARIABLE,
  EMPTY,
  getAprDisplay,
  getMaxAmountDisplay,
  getMonthlyPaymentDisplay,
  getPreviewAprDisplay,
  getPreviewTermUnits,
  LEGAL,
  LOAN_AMOUNT_PREFIX,
  MONTHLY_PAYMENT_LABEL,
  OPEN_TERM_LABEL,
  OPEN_TERM_LENGTH,
  PREVIEW_APR_LABEL,
  PREVIEW_MONTHLY_PAYMENT_LABEL,
  PREVIEW_TERM_LABEL,
} from 'embed/utils/offer-display-utils'

import { getContrastingColor } from 'embed/utils'
import { LoanOffer } from 'embed/constants/models'

import { CoApplicantOfferBadge } from '../../CoApplicantOfferBadge'
import { Flag } from './flag'

const styles = require('embed/styles/results')
const previewStyles = require('embed/styles/preview-offers')
interface Props {
  aprSuffix?: JSX.Element | string
  bestOfferId?: string
  disclaimer: React.ReactNode
  hideCta: boolean
  isResultsView?: boolean
  monthlyPaymentSuffix?: React.ReactNode
  offer: LoanOffer
  onLoanOfferCtaClick: (event: React.MouseEvent<Element>) => void
  partnerLogo: React.ReactNode
  selectedStep: LoanStep
  setEl: (el: HTMLLIElement) => void
  shouldDisplayTransunionDisclaimer: boolean
  tooltipText?: string
  trackingPixel: React.ReactNode
  viewportWidth: number
}

const ListItemFullRanged: React.SFC<Props> = ({
  aprSuffix,
  bestOfferId,
  disclaimer,
  hideCta,
  isResultsView,
  monthlyPaymentSuffix,
  offer,
  offer: { productSubType },
  onLoanOfferCtaClick,
  partnerLogo,
  selectedStep,
  setEl,
  shouldDisplayTransunionDisclaimer,
  tooltipText,
  trackingPixel,
  viewportWidth,
}) => {
  const flag = (
    <div className={styles.offerFlagContainer}>
      <Flag
        bestOfferId={bestOfferId}
        offer={offer}
        shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
        tooltipText={tooltipText}
        viewportWidth={viewportWidth}
      />
    </div>
  )
  const paymentAmount = getMonthlyPaymentDisplay(offer)
  const shouldShowOfferAmount = selectedStep === LoanStep.Results && offer.maxAmount
  const showCoApplicantOfferBadge =
    offer.coApplicant && viewportWidth <= coAppBadgeBreakpoint && offer.uuid === bestOfferId
  const coApplicantOfferBadge = showCoApplicantOfferBadge && <CoApplicantOfferBadge />

  const isTransunionSupply: boolean = configCompanyUuid === TRANSUNION_COMPANY_UUID

  let loanOfferTermlabel = PREVIEW_TERM_LABEL
  let loanOfferTermLength: JSX.Element | string = (
    <>
      {offer.termLength} <sup>{getPreviewTermUnits(offer)}</sup>
    </>
  )

  if (offer.termUnit === TermUnit.Open) {
    loanOfferTermlabel = OPEN_TERM_LABEL
    loanOfferTermLength = OPEN_TERM_LENGTH
  }

  const aprPrefix =
    isResultsView && offer.aprType && `${offer.aprType === AprType.Variable ? APR_VARIABLE : APR_FIXED} `
  const aprLabel = isResultsView ? APR_LABEL : PREVIEW_APR_LABEL
  const aprDisplay = isResultsView ? getAprDisplay(offer.meanApr) : getPreviewAprDisplay(offer)

  return (
    <ThemeConsumer>
      {({
        borderRadiusLarge,
        borderRadiusSmall,
        colorBorder,
        ctaStyles,
        colorTextPrimary,
        colorTextSecondary,
        fontFamilyPrimary,
      }) => (
        <li
          data-test-val={isDev ? offer.uuid : ''}
          className={classnames(
            PublicClassNames.Offer,
            styles.offer,
            css`
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          ref={setEl}
        >
          {trackingPixel}
          {flag}
          {coApplicantOfferBadge}
          <div className={classnames(styles.offerWrapper, styles.loanOfferWrapper)}>
            <div className={classnames(styles.column, styles.originator)}>{partnerLogo}</div>
            <table>
              <tbody>
                <tr>
                  <td className={classnames(previewStyles.previewLabel)}>
                    <p className={classnames(css(`color: ${colorTextSecondary}`))}>
                      {aprPrefix}
                      {aprLabel}
                      {aprSuffix}
                    </p>
                  </td>
                  <td className={classnames(previewStyles.previewLabel)}>
                    <p className={classnames(css(`color: ${colorTextSecondary}`))}>
                      {loanOfferTermlabel}
                      {offer.originator && offer.originator.key === 'light-stream' ? LEGAL : EMPTY}
                    </p>
                  </td>
                  <td className={classnames(previewStyles.previewLabel)}>
                    {isResultsView && (offer.meanMonthlyPayment || offer.monthlyPayment) && (
                      <p
                        data-test-id="monthly_payment"
                        className={classnames(styles.label, css(`color: ${colorTextSecondary}`))}
                      >
                        {MONTHLY_PAYMENT_LABEL}
                        {monthlyPaymentSuffix}
                      </p>
                    )}
                    {!isResultsView && offer.maxAmount && (
                      <p className={classnames(styles.monthlyPaymentLabel, css(`color: ${colorTextSecondary}`))}>
                        {isResultsView ? MONTHLY_PAYMENT_LABEL : PREVIEW_MONTHLY_PAYMENT_LABEL}
                        {monthlyPaymentSuffix}
                      </p>
                    )}
                  </td>
                </tr>
                <tr>
                  <td
                    data-test-id="offer_apr"
                    className={classnames(
                      styles.stat,
                      previewStyles.previewStat,
                      styles.previewAprStat,
                      css`
                        font-family: ${fontFamilyPrimary};
                      `,
                    )}
                  >
                    {aprDisplay}
                    {APR_POSTFIX}
                  </td>
                  <td
                    data-test-id="offer_term"
                    className={classnames(
                      styles.stat,
                      previewStyles.previewStat,
                      css`
                        font-family: ${fontFamilyPrimary};
                      `,
                    )}
                  >
                    {loanOfferTermLength}
                  </td>
                  {!isResultsView && offer.maxAmount && (
                    <td
                      data-test-id="offer_payment"
                      className={classnames(
                        styles.stat,
                        previewStyles.previewStat,
                        css`
                          font-family: ${fontFamilyPrimary};
                        `,
                      )}
                    >
                      {getMaxAmountDisplay(offer, LOAN_AMOUNT_PREFIX)}
                    </td>
                  )}
                  {isResultsView && (
                    <td
                      data-test-id="offer_payment"
                      className={classnames(
                        styles.stat,
                        previewStyles.previewStat,
                        css`
                          font-family: ${fontFamilyPrimary};
                        `,
                      )}
                    >
                      {paymentAmount}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
            {!hideCta && (
              <div className={classnames(previewStyles.cta, styles.cta)}>
                {shouldShowOfferAmount && (
                  <p data-test-id="offer_maxAmount" className={classnames(styles.stat, styles.offerAmount)}>
                    {LOAN_AMOUNT_PREFIX}
                    {getMaxAmountDisplay(offer)}
                  </p>
                )}
                <button
                  data-test-id="offer_continue"
                  className={classnames(
                    styles.button,
                    css(`
                    color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                    border-radius: ${borderRadiusSmall};
                    &:hover {opacity: 0.84;}
                  `),
                  )}
                  style={ctaStyles}
                  onClick={onLoanOfferCtaClick}
                >
                  {isResultsView ? 'Continue' : 'Get Matched'}
                </button>
                {isTransunionSupply && isResultsView && (
                  <div
                    className={classnames(
                      styles.tuDisclaimer,
                      css`
                        color: ${colorTextSecondary};
                      `,
                    )}
                  >
                    This is not a guaranteed approval
                  </div>
                )}
              </div>
            )}
          </div>
          <div>{disclaimer}</div>
        </li>
      )}
    </ThemeConsumer>
  )
}

export default ListItemFullRanged
